import {
  types,
  Instance,
  IAnyType,
  IModelType,
  getRoot,
  resolveIdentifier,
  getIdentifier,
  ISimpleType,
  getParentOfType
} from 'mobx-state-tree'
import { pathC } from 'src/components/viewer/components/paths/enclosurePaths'
import { default as sides, top, right, bottom, left } from 'src/constants/sides'
import { IValueNumberWithUnit, IValueNumberWithLimits } from './types'

export const Unit = types.enumeration(['mm', 'in'])
export const Side = types.enumeration([...sides])

export const NumberValue = types
  .model('Number', { value: types.number })
  .actions((self) => ({
    update(value: number, patch: boolean = true) {
      self.value = Number(value)
    }
  }))

export const UnitNumberValue: IValueNumberWithUnit = NumberValue.named(
  'UnitNumber'
)
  .props({
    unit: types.optional(Unit, 'mm')
  })
  .views((self) => ({
    get displayValue() {
      return self.unit === 'in' ? self.value * 0.0393700787 : self.value
    }
  }))
  .actions((self) => ({
    selectUnit(unit: typeof self.unit, patch: boolean = true) {
      self.unit = unit
    }
  }))

const OptionalValue = types.optional(UnitNumberValue, { value: 0, unit: 'mm' })

export const Border = types.model(
  [top, right, bottom, left].reduce(
    (p, c) => ({ ...p, [c]: OptionalValue }),
    {}
  ) as {
    [key in
      | typeof top
      | typeof right
      | typeof bottom
      | typeof left]: typeof OptionalValue
  }
)

export const StringValue = types
  .model('String', { value: types.string })
  .actions((self) => ({
    update(value: string, patch: boolean = true) {
      self.value = value
    }
  }))

export const TextValue = StringValue.named('Text')

export const generateEnumValue = <V extends string | number>(values: V[]) => {
  return types
    .model({
      value: types.union(...values.map((value) => types.literal(value)))
    })
    .views((self) => ({
      get options() {
        return values
      }
    }))
    .actions((self) => ({
      update(value: number | string) {
        self.value = value
      }
    }))
}

interface EnumerationReferenece {
  value: any
}

export const generateEnumReferenceValue = <
  Values extends Array<any> = string[] | number[],
  Value extends Unpacked<Values> = Unpacked<Values>
>(
  type: IModelType<
    { [key: string]: typeof types.identifier } & { [key: string]: IAnyType },
    { identifierAttribute: string }
  >,
  options: Values,
  defaultValue?: Value,
  name?: string,
  displayValueAttribute = type.identifierAttribute
) => {
  const value = types.reference(type)
  return types
    .model(name || 'EnumReference', {
      value: defaultValue ? types.optional(value, defaultValue) : value
    })
    .views((self) => {
      const root = getRoot(self)
      const identifier = getIdentifier(self.value)
      return {
        get displayValue() {
          return displayValueAttribute && self.value[displayValueAttribute]
        },
        get options() {
          const identifierAttribute = type.identifierAttribute
          if (identifierAttribute) {
            const result: typeof identifierAttribute[] = []
            options.forEach((option) => {
              const instance = resolveIdentifier(type, root, option)
              if (instance && instance[identifierAttribute]) {
                result.push(instance[identifierAttribute])
              }
            })
            return result
          }
          return
        }
      }
    })
    .actions((self) => ({
      update(value: any) {
        self.value = value
      }
    }))
}

type GenerateValueTypeWithLimitsArg = {
  defaultValue?: number
} & {
  [key in 'min' | 'max']?: number
} & { getRange?: (self: Instance<any>) => { [key in 'min' | 'max']: number } }

export const generateValueTypeWithLimits = ({
  min = 0,
  max = Infinity,
  getRange,
  defaultValue = 0
}: GenerateValueTypeWithLimitsArg): IValueNumberWithLimits => {
  const checkValue = (
    value: number,
    range: { [key in 'min' | 'max']: number | ISimpleType<number> } = {
      min,
      max
    }
  ) =>
    value >= range.min && range.max !== undefined ? value <= range.max : true

  const refinedValue = types.refinement(types.number, checkValue)

  return UnitNumberValue.props({
    value: defaultValue
      ? types.optional(refinedValue, defaultValue)
      : refinedValue
  })
    .views((self) => {
      return {
        get min() {
          return getRange?.(self).min || min
        },
        get max() {
          return getRange?.(self).max || max
        }
      }
    })
    .actions((self) => {
      return {
        update(value: number) {
          if (checkValue(value, { min: self.min, max: self.max })) {
            self.value = value
          }
        }
      }
    })
}
